/* eslint-disable no-param-reassign */
import { store } from "../../store";
import { Type } from "../helper";
import { getDisplayName as getContactPersonDisplayName } from "../../../components/Input/ContactPersonInput";
import {
    getAccountName, getContactDisplayName, getFieldName,
    getPaymentMethodName, getTagName, getTermName,
} from "../../List/helper/getListItemNameHelper";
import {
    getItemNames, getBankItemNames, getDepositItemNames,
    getFormItemNames, getTermItemNames,
} from "./getAutosaveTransactionItemNames";
import { SubscriptionCheck } from "../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";

/**
 * If no UOM/location subscription, FormItem helper.js will remove the product_unit_id / location_id.
 * But for autosaving purposes, we'll still store the values, so that on restore, the values will still appear.
 * @param {*} txnFormItem current form data item
 * @param {*} autosaveFormItems to-be-autosaved form item
 */
const iterFormItems = (txnFormItem, autosaveFormItem) => {
    const hasUOMSubscription = SubscriptionCheck
        .withKeys(SubscriptionScopeKey["PRODUCT.MULTI_UOM"]).hasSubscriptionTo();
    const hasMultiLocationSubscription = SubscriptionCheck
        .withKeys(SubscriptionScopeKey["INVENTORY.MULTI_LOCATION"]).hasSubscriptionTo();

    if (!hasUOMSubscription && (txnFormItem.product_unit_id || txnFormItem.product_unit_id === 0))
        autosaveFormItem.product_unit_id = txnFormItem.product_unit_id;

    if (!hasMultiLocationSubscription && (txnFormItem.location_id || txnFormItem.location_id === 0))
        autosaveFormItem.location_id = txnFormItem.location_id;
};

/**
 * This function is used in each transaction's helper.js, to parse the autosave data before
 * storing it into local storage.
 * For those that are stored in IDs, eg contact_id, account_id, we store its corresponding name/label as well,
 * inside autosave_text_values object.
 *
 * Eg, autosave_text_values: {
 *      contact_id: "Cash Sales",
 *      tag_ids: ["Tag 1", "Another tag"],
 *      form_items: [{
 *          account_id: "Sales Income",
 *          autosave_id: 0, // this *may* be used to map with the corresponding autosaveData.form_items item (will priority using index finding first)
 *          location_id: "Bukit Bintang",
 *          product_id: "Apple",
 *          product_unit_id: "unit",
 *          tax_code_id: "SL5"
 *      }]
 * }
 */
export const normalizeAutosaveData = (transactionType, autosaveData, props = {}) => {
    const { List } = store.getState();

    const {
        formItems,
        linkItems,
        depositItems,

        // For Contras
        linkItemsSales,
        linkItemsPurchases,
    } = props;

    // ----- STEP 1: Save all id fields in their text form, eg contacts, tags etc.
    // This is used to inform the users which values are no longer applicable (if any) when restoring data.
    const autosave_text_values = {};

    // 1.1: Contacts
    if (autosaveData.contact_id)
        autosave_text_values.contact_id = getContactDisplayName(autosaveData.contact_id);

    if (autosaveData.contact2_id)
        autosave_text_values.contact2_id = getContactDisplayName(autosaveData.contact2_id);

    // 1.2: Accounts
    if (autosaveData.account_id)
        autosave_text_values.account_id = getAccountName(autosaveData.account_id);

    if (autosaveData.account2_id)
        autosave_text_values.account2_id = getAccountName(autosaveData.account2_id);

    // 1.3: Contact persons
    if (autosaveData.billing_contact_person_id && !autosaveData.billing_contact_person) {
        const cp = List.contactPersons.find(cp => cp.id === autosaveData.billing_contact_person_id);
        if (cp)
            autosave_text_values.billing_contact_person_id = getContactPersonDisplayName(cp);
    }
    if (autosaveData.shipping_contact_person_id && !autosaveData.shipping_contact_person) {
        const cp = List.contactPersons.find(cp => cp.id === autosaveData.shipping_contact_person_id);
        if (cp)
            autosave_text_values.shipping_contact_person_id = getContactPersonDisplayName(cp);
    }

    // 1.4: Tag ids
    if (autosaveData.tag_ids)
        autosave_text_values.tag_ids = autosaveData.tag_ids.map(id => (getTagName(id)));

    // 1.5: Term id
    if (autosaveData.term_id)
        autosave_text_values.term_id = getTermName(autosaveData.term_id);

    // 1.6: Payment method id
    if (autosaveData.payment_method_id)
        autosave_text_values.payment_method_id = getPaymentMethodName(autosaveData.payment_method_id);

    // 1.7: Custom fields
    if (autosaveData.fields && autosaveData.fields.length !== 0)
        autosave_text_values.fields = autosaveData.fields.map(obj => (getFieldName(obj)));

    // 1.7: Transaction items
    if (autosaveData.bank_items || autosaveData.journal_items) {
        const dataIndex = autosaveData.bank_items ? 'bank_items' : 'journal_items';
        autosave_text_values[dataIndex] = [];
        autosaveData[dataIndex] = autosaveData[dataIndex].map((item, idx) => {
            // item.autosave_id = idx;
            autosave_text_values[dataIndex].push(getBankItemNames(item, idx));
            return {
                ...item,
                autosave_id: idx,
            };
        });
    }

    if (autosaveData.deposit_items) {
        autosave_text_values.deposit_items = [];
        autosaveData.deposit_items = autosaveData.deposit_items.map((item, idx) => {
            // item.autosave_id = idx;
            autosave_text_values.deposit_items.push(getDepositItemNames(item, idx));
            return {
                ...item,
                autosave_id: idx,
            };
        });
    }

    if (autosaveData.form_items) {
        autosave_text_values.form_items = [];
        autosaveData.form_items = autosaveData.form_items.map((item, idx) => {
            // item.autosave_id = idx;
            const obj = getFormItemNames(item, idx);

            // handle bundle
            if (item.children) {
                obj.children = [];
                item.children = item.children.map((child, childIdx) => {
                    // child.autosave_id = childIdx;
                    obj.children.push(getFormItemNames(child, childIdx));

                    return {
                        ...child,
                        autosave_id: childIdx,
                    };
                });
            }
            autosave_text_values.form_items.push(obj);
            return {
                ...item,
                autosave_id: idx,
            };
        });
    }

    if (autosaveData.items) { // stock adjustment and transfer
        autosave_text_values.items = [];
        autosaveData.items = autosaveData.items.map((item, idx) => {
            // item.autosave_id = idx;
            autosave_text_values.items.push(getItemNames(item, idx));
            return {
                ...item,
                autosave_id: idx,
            };
        });
    }

    if (autosaveData.term_items) {
        autosave_text_values.term_items = [];
        autosaveData.term_items = autosaveData.term_items.map((item, idx) => {
            // item.autosave_id = idx;
            autosave_text_values.term_items.push(getTermItemNames(item, idx));
            return {
                ...item,
                autosave_id: idx,
            };
        });
    }

    // Finally, attach the object with all the names into the autosaveData
    autosaveData.autosave_text_values = autosave_text_values;

    // ----- STEP 2: Handle transaction items.
    // 2.1: Save the transfer_transaction too so that form item table will
    // display the "This item is transferred from .." tag.
    // For form_items, we can straight away modify the object here, since in the step above we already created a new array for it
    if (formItems) {
        formItems.forEach((item, idx) => {
            if (item.transfer_transaction)
                autosaveData.form_items[idx].transfer_transaction = item.transfer_transaction;

            if (item.children) {
                item.children.forEach((child, childIdx) => {
                    iterFormItems(child, autosaveData.form_items[idx].children[childIdx]);
                });
            }

            iterFormItems(item, autosaveData.form_items[idx]);
        });
    }

    // 2.2: Save the number for link items, to be used to inform if the items are no longer applicable
    if (linkItems && autosaveData.link_items) {
        autosaveData.link_items = autosaveData.link_items.map(item => ({
            ...item,
            number: linkItems.find(i => i.target_transaction_id === item.target_transaction_id).number,
        }));
    }

    if (linkItemsSales && autosaveData.sales_link_items) {
        autosaveData.sales_link_items = autosaveData.sales_link_items.map(item => ({
            ...item,
            number: linkItemsSales.find(i => i.target_transaction_id === item.target_transaction_id).number,
        }));
    }

    if (linkItemsPurchases && autosaveData.purchase_link_items) {
        autosaveData.purchase_link_items = autosaveData.purchase_link_items.map(item => ({
            ...item,
            number: linkItemsPurchases.find(
                i => i.target_transaction_id === item.target_transaction_id).number,
        }));
    }

    // 2.3: If deposit items first line is autofill, save the line num, don't save the amount, as when restoring we want it to be as autofill too.
    // Line num will be consumed in DepositItemHelper.normalizeData.
    // For deposit_items, we can straight away modify the object here, since in the step above we already created a new array for it
    if (depositItems) {
        const firstLineDepositItem = depositItems.find(i => i.line === 1);
        if (firstLineDepositItem && !firstLineDepositItem.amount) {
            autosaveData.deposit_items[0].amount = null;
            autosaveData.deposit_items[0].line = 1;
        }
    }

    // 2.4: Don't save apply_amount for these link/linked items, since we want them to be dynamic when restoring
    if ([Type.sale_invoice, Type.purchase_bill].includes(transactionType) && autosaveData.linked_items) {
        autosaveData.linked_items = autosaveData.linked_items.map(linked => ({
            ...linked,
            apply_amount: undefined,
        }));
    }

    // if (transactionType === Type.tax_deemed_payment && autosaveData.link_items) {
    //     autosaveData.link_items = autosaveData.link_items.map(item => ({
    //         ...item,
    //         apply_amount: undefined,
    //     }));
    // }
};
